import { useState, useEffect } from "react";
import { auth } from "../firebaseConfig";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import AuthLayout from "../components/AuthLayout";
import endpoints from "../apiConfig";
import '../styles/Auth.css';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: ""
  });
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    window.addEventListener('unhandledrejection', function(event) {
      console.error('UNHANDLED PROMISE REJECTION:', event.reason);
    });
    return () => {
      window.removeEventListener('unhandledrejection', () => {});
    };
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    setMessage("");
    setIsLoading(true);

    try {
      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const token = await userCredential.user.getIdToken();
      
      // Send email verification
      try {
        await sendEmailVerification(userCredential.user);
        console.log("📧 Email verification sent to:", formData.email);
      } catch (verificationError) {
        console.error("⚠️ Failed to send email verification:", verificationError);
        // Don't block signup if verification email fails
      }
      
      console.log("✅ Firebase user created, initializing user profile...", {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        firebase_uid: userCredential.user.uid
      });

      // Use axios directly to avoid interceptor conflicts
      const baseURL = import.meta.env.PROD 
        ? 'https://art-emerge-4744deb5e11a.herokuapp.com/api'
        : 'http://localhost:5051/api';

      const response = await axios.post(`${baseURL}/users/initialize`, {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        firebase_uid: userCredential.user.uid
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log("✅ User profile initialized!");
      setMessage("🎉 Signup successful! Check your email for verification link. Redirecting...");
      
      // Store the user data and token
      localStorage.setItem('user', JSON.stringify(response.data));
      localStorage.setItem('token', token);
      
      // Call /api/users/sync to create Shopify customer (with marketing opt-in on server)
      console.log('➡️ Attempting to sync user with Shopify...');
      let syncSuccess = false;
      try {
        const syncRes = await axios.post(`${baseURL}/users/sync`, { role: formData.role }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        console.log('✅ Synced user with Shopify (customer created or updated)', syncRes.data);
        syncSuccess = true;
      } catch (syncError) {
        console.error('❌ Error syncing user with Shopify:', syncError);
        const isDevelopment = !import.meta.env.PROD;
        
        if (isDevelopment) {
          console.log('🔧 Development mode: Shopify sync failed but continuing...');
          setMessage('🎉 Signup successful! Check your email for verification link. (Shopify sync skipped in development)');
          syncSuccess = true; // Allow continuation in development
        } else {
          setMessage('Failed to sync with Shopify: ' + (syncError.response?.data?.error || syncError.message));
        }
      }
      
      setTimeout(() => {
        setIsLoading(false);
        if (syncSuccess) {
          navigate('/dashboard');
        }
      }, 500);
    } catch (error) {
      console.error("❌ Signup error:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
      
      if (error.code === 'auth/email-already-in-use') {
        setMessage('Email is already in use. Please log in instead.');
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message;
        setMessage(`Failed to create account: ${errorMessage}`);
      }
    }
  };

  return (
    <AuthLayout title="Create your account">
      {message && (
        <p className={`auth-error ${message.includes('🎉') ? 'success' : ''}`}>
          {message}
        </p>
      )}
      <form className="auth-form" onSubmit={handleSignup}>
        <div className="form-group">
          <input 
            type="text" 
            name="name" 
            placeholder="Full Name" 
            onChange={handleChange} 
            required 
            disabled={isLoading}
          />
        </div>
        <div className="form-group">
          <input 
            type="email" 
            name="email" 
            placeholder="Email" 
            onChange={handleChange} 
            required 
            disabled={isLoading}
          />
        </div>
        <div className="form-group">
          <input 
            type="password" 
            name="password" 
            placeholder="Password" 
            onChange={handleChange} 
            required 
            disabled={isLoading}
          />
        </div>
        
        <div className="form-group">
          <label>Select Your Role:</label>
          <select 
            name="role" 
            onChange={handleChange} 
            value={formData.role} 
            disabled={isLoading}
            required
          >
            <option value="" disabled>Select a role</option>
            <option value="artist">Artist</option>
            <option value="gallery">Gallery</option>
            <option value="curator">Curator</option>
            <option value="collector">Collector</option>
          </select>
        </div>

        <button type="submit" className="auth-button" disabled={isLoading}>
          {isLoading ? 'Creating account...' : 'Create Account'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default Signup;
